import React from 'react';
import { Alert, AlertTitle, Box, Button, Chip, Divider, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import { ISubmissionVehicleResponse } from '../../../../model/ser/submission/response/submission-vehicle-response';
import { SUBMISSION_TYPE_LABEL, SubmissionType, getFileTypeByName } from '../../../../model/ser/ser-enums';
import { formatDate } from '../../../../utils/date';
import { formatFileSize } from '../../../../utils/format';
import { FileTypeIcon } from '../../components/file-type-icon';
import { SendStep, SubmissionDraft, computeVehicleDeadline, weekDayName } from '../submission-send-rules';
import { StepSection } from './step-section';
import { VehicleDeadlineLine } from './vehicle-deadline-line';

function ReviewBlock({ title, onEdit, children }: { title: string; onEdit: () => void; children: React.ReactNode }) {
    return (
        <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>{title}</Typography>
                <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={onEdit}>Editar</Button>
            </Box>
            {children}
        </Box>
    );
}

// Avisos de prazo/dia dos veículos escolhidos para a data informada
export function getDraftWarnings(draft: SubmissionDraft, vehicles: ISubmissionVehicleResponse[]): string[] {
    return vehicles
        .filter((v) => draft.vehicleIds.includes(v.id))
        .flatMap((vehicle) => {
            const deadline = computeVehicleDeadline(vehicle, draft.publishDate);
            if (vehicle.isDefault && deadline.expired) return [`O prazo de envio do ${vehicle.name} encerrou para esta data. Escolha outra data de publicação.`];
            if (!deadline.publishesOnDay) return [`${vehicle.name} não publica em ${weekDayName(dayjs(draft.publishDate))}.`];
            return [];
        });
}

export function ReviewStep({ draft, vehicles, onEdit }: { draft: SubmissionDraft; vehicles: ISubmissionVehicleResponse[]; onEdit: (step: SendStep) => void }) {
    const warnings = getDraftWarnings(draft, vehicles);
    const selectedVehicles = vehicles.filter((v) => draft.vehicleIds.includes(v.id));
    const totalSize = draft.files.reduce((sum, file) => sum + file.size, 0);
    const publishDate = draft.publishDate ? dayjs(draft.publishDate) : null;

    return (
        <StepSection title="Revisão" description="Confira os dados antes de enviar. Depois do envio, a remessa só pode ser cancelada.">
            {draft.apiErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <AlertTitle>A remessa foi recusada</AlertTitle>
                    {draft.apiErrors.map((message) => <Typography key={message} variant="body2">{message}</Typography>)}
                </Alert>
            )}
            {warnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>Atenção à data de publicação</AlertTitle>
                    {warnings.map((message) => <Typography key={message} variant="body2">{message}</Typography>)}
                </Alert>
            )}

            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2.5 }}>
                <ReviewBlock title="Dados" onEdit={() => onEdit(SendStep.Data)}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ wordBreak: 'break-word' }}>{draft.title}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                        <Chip size="small" color={draft.type === SubmissionType.Urgencia ? 'warning' : 'default'} label={SUBMISSION_TYPE_LABEL[draft.type]} />
                        <Typography variant="body2" color="text.secondary">
                            Publicação em {formatDate(draft.publishDate)}{publishDate ? ` (${weekDayName(publishDate)})` : ''}
                        </Typography>
                    </Box>
                    {draft.instruction.trim() && (
                        <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-wrap', color: 'text.secondary' }}>{draft.instruction.trim()}</Typography>
                    )}
                </ReviewBlock>
                <Divider />
                <ReviewBlock title={`Arquivos · ${draft.files.length} · ${formatFileSize(totalSize)}`} onEdit={() => onEdit(SendStep.Files)}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {draft.files.map((file, index) => (
                            <Box key={`${file.name}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                <FileTypeIcon type={getFileTypeByName(file.name)} size={28} />
                                <Typography variant="body2" noWrap sx={{ flex: 1 }} title={file.name}>{file.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{formatFileSize(file.size)}</Typography>
                            </Box>
                        ))}
                    </Box>
                </ReviewBlock>
                <Divider />
                <ReviewBlock title={`Veículos · ${selectedVehicles.length}`} onEdit={() => onEdit(SendStep.Vehicles)}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {selectedVehicles.map((vehicle) => (
                            <Box key={vehicle.id}>
                                <Typography variant="body2" fontWeight={600}>{vehicle.name}</Typography>
                                <VehicleDeadlineLine deadline={computeVehicleDeadline(vehicle, draft.publishDate)} publishDate={draft.publishDate} />
                            </Box>
                        ))}
                    </Box>
                </ReviewBlock>
            </Box>
        </StepSection>
    );
}
